import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  initialPaginationState,
  mergeIds,
  PaginationState,
} from '@redux/helpers';
import { TeamType } from '@typings/participant';
import { RequestToSpeak } from '@typings/requestToSpeak';
import { LISTENERS_PER_PAGE } from '@utils/constants';

type InitialState = {
  byId: Record<string, RequestToSpeak>;
  byTeam: {
    [TeamType.Blue]: PaginationState;
    [TeamType.Red]: PaginationState;
    [TeamType.Neutral]: PaginationState;
  };
  leftRequestsCount: number;
  rightRequestsCount: number;
  neutralRequestsCount: number;
};

export const initialState: InitialState = {
  byId: {},
  byTeam: {
    [TeamType.Blue]: initialPaginationState,
    [TeamType.Red]: initialPaginationState,
    [TeamType.Neutral]: initialPaginationState,
  },
  leftRequestsCount: 0,
  rightRequestsCount: 0,
  neutralRequestsCount: 0,
};

export const requestsToSpeakSlice = createSlice({
  name: 'requestsToSpeak',
  initialState,
  reducers: {
    setManyRequestsToSpeakDataById: (
      state,
      action: PayloadAction<Record<string, RequestToSpeak>>,
    ) => {
      Object.entries(action.payload).forEach(([id, requestToSpeak]) => {
        const previousData = state.byId[id] || {};
        state.byId[id] = { ...previousData, ...requestToSpeak };
      });
    },
    setRequestToSpeakDataById: (
      state,
      action: PayloadAction<Partial<RequestToSpeak>>,
    ) => {
      const requestToSpeak = action.payload;
      const oldRequestToSpeak = state.byId[requestToSpeak.id!] || {};
      state.byId[requestToSpeak.id!] = {
        ...oldRequestToSpeak,
        ...requestToSpeak,
      };
    },
    setManyRequestToSpeakByTeam: (
      state,
      action: PayloadAction<{
        team: TeamType;
        ids: string[];
        isRefresh?: boolean;
      }>,
    ) => {
      const { team, ids, isRefresh = false } = action.payload;
      const mayLoadMore = ids.length === LISTENERS_PER_PAGE;
      const oldIds = state.byTeam[team]?.ids || [];

      state.byTeam[team] = {
        ids: mergeIds(oldIds, ids, isRefresh),
        mayLoadMore,
      };
    },
    removeRequestToSpeakByTeam: (
      state,
      action: PayloadAction<RequestToSpeak>,
    ) => {
      const { id, team } = action.payload;
      const oldIds = state.byTeam[team]?.ids || [];

      state.byTeam[team].ids = [...oldIds].filter(
        (requestId) => requestId !== id,
      );
    },
    setRequestsToSpeakCount: (
      state,
      action: PayloadAction<{
        leftRequestsCount: number;
        rightRequestsCount: number;
      }>,
    ) => {
      state.leftRequestsCount = action.payload.leftRequestsCount;
      state.rightRequestsCount = action.payload.rightRequestsCount;
    },
    setNeutralRequestsToSpeakCount: (
      state,
      action: PayloadAction<{
        neutralRequestsCount: number;
      }>,
    ) => {
      state.neutralRequestsCount = action.payload.neutralRequestsCount;
    },
    decreaseRequestsToSpeakCount: (state, action: PayloadAction<TeamType>) => {
      switch (action.payload) {
        case TeamType.Blue:
          state.leftRequestsCount = state.leftRequestsCount
            ? state.leftRequestsCount - 1
            : 0;
          break;
        case TeamType.Red:
          state.rightRequestsCount = state.rightRequestsCount
            ? state.rightRequestsCount - 1
            : 0;
          break;
        case TeamType.Neutral:
          state.neutralRequestsCount = state.neutralRequestsCount
            ? state.neutralRequestsCount - 1
            : 0;
          break;
      }
    },
    increaseRequestsToSpeakCount: (state, action: PayloadAction<TeamType>) => {
      switch (action.payload) {
        case TeamType.Blue:
          state.leftRequestsCount = state.leftRequestsCount + 1;
          break;
        case TeamType.Red:
          state.rightRequestsCount = state.rightRequestsCount + 1;
          break;
        case TeamType.Neutral:
          state.neutralRequestsCount = state.neutralRequestsCount + 1;
          break;
      }
    },
  },
});

export const {
  setManyRequestsToSpeakDataById,
  setRequestToSpeakDataById,
  setManyRequestToSpeakByTeam,
  removeRequestToSpeakByTeam,
  setRequestsToSpeakCount,
  setNeutralRequestsToSpeakCount,
} = requestsToSpeakSlice.actions;

export default requestsToSpeakSlice.reducer;
